<template>
	<div>
		<modal
			class="sell-product-modal"
			:size="'medium'"
			v-if="show"
			:hideFooter="true"
			@close="!isModalLoading && handleClose()">
			<div slot="header">
				<h3>
					<i class="material-icons">store</i>
					Jual Produk
				</h3>
			</div>

			<!-- Loading Overlay -->
			<div v-if="isModalLoading" class="modal-loading-overlay">
				<div class="modal-loading-content">
					<div class="spinner-border" role="status">
						<span class="sr-only">Loading...</span>
					</div>
					<p class="mt-3 mb-0">Processing...</p>
				</div>
			</div>

			<div slot="body" class="sell-product-modal__body" :class="{ 'blur-content': isModalLoading }">
				<div class="platform-selection">
					<p class="platform-selection__subtitle">
						Pilih platform tujuan untuk menjual produk ini.
					</p>
					<div class="platform-selection__grid">
						<div
							v-for="platform in availablePlatforms"
							:key="platform.key"
							class="platform-card"
							:class="{ 'platform-card--disabled': !platform.isEnabled }"
							role="button"
							tabindex="0"
							@click="selectPlatform(platform)">
							<div class="platform-card__image">
								<img
									v-if="platform.icon"
									:src="platform.icon"
									:alt="`${platform.label} logo`">
								<div v-else class="platform-card__placeholder">
									<i class="material-icons">store</i>
								</div>
							</div>
							<div class="platform-card__content">
								<div class="platform-card__title">
									<span>{{ platform.label }}</span>
									<span
										class="platform-card__badge platform-card__badge--soon"
										v-if="platform.isComingSoon && getBadgeText(platform)">
										{{ getBadgeText(platform) }}
									</span>
								</div>
								<p class="platform-card__description">
									{{ platform.description }}
								</p>
							</div>
							<div class="platform-card__action">
								<button
									v-if="shouldShowButton(platform)"
									type="button"
									class="btn btn-primary btn-sm"
									:disabled="!platform.isEnabled"
									:class="{ 'is-loading': platform.isLoading }">
									<span v-if="platform.isLoading" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
									{{ getButtonText(platform) }}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</modal>
	</div>
</template>

<script>
import { mapActions } from 'vuex';
import Modal from './Modal.vue';
import { getAxiosErrorMessage } from '@/lib/helper';
import orderonlineApi from '@/api/orderonline';

export default {
	name: 'SellProductModal',
	components: {
		Modal,
	},
	props: {
		show: {
			type: Boolean,
			default: false,
		},
		platforms: {
			type: Array,
			default: () => [],
		},
		productId: {
			type: [String, Object],
			default: null,
		},
		productName: {
			type: String,
			default: '',
		},
		productSlug: {
			type: String,
			default: '',
		},
		productUserId: {
			type: [String, Object],
			default: null,
		},
		isProductAddedToOO: {
			type: Boolean,
			default: false,
		},
		marketplaceStatus: {
			type: String,
			default: '',
		},
	},
	data() {
		return {
			defaultPlatforms: [
				{
					key: 'orderonline',
					label: 'Orderonline',
					description: 'Segera jual produk lewat Orderonline.',
					icon: '/images/icon-orderonline.png',
					isEnabled: true,
					isComingSoon: false,
					isLoading: false,
				},
				{
					key: 'shopee',
					label: 'Shopee',
					description: 'Jual produk di marketplace Shopee.',
					icon: '/images/icon-shopee.png',
					isEnabled: true,
					isComingSoon: false,
					isLoading: false,
				},
				{
					key: 'tiktok',
					label: 'TikTok Shop',
					description: 'Segera integrasikan dengan TikTok Shop.',
					icon: '/images/icon-tiktok.png',
					isEnabled: false,
					isComingSoon: true,
					isLoading: false,
				},
			],
			isAddingToOrderonline: false,
		};
	},
	computed: {
		isModalLoading() {
			return this.isAddingToOrderonline;
		},
		isMarketplaceEnabled() {
			return this.marketplaceStatus === 'approved';
		},
		availablePlatforms() {
			const platforms = this.platforms.length ? this.platforms : this.defaultPlatforms;

			// Update platform states based on loading and marketplace status
			return platforms.map((platform) => {
				if (platform.key === 'orderonline') {
					return {
						...platform,
						isEnabled: !this.isAddingToOrderonline,
						isLoading: this.isAddingToOrderonline,
					};
				}

				// Disable non-orderonline platforms if marketplace is not enabled
				if (!this.isMarketplaceEnabled) {
					return {
						...platform,
						isEnabled: false,
						isComingSoon: false,
						description: 'Tidak Tersedia Untuk Produk Ini',
					};
				}

				return platform;
			});
		},
	},
	methods: {
		...mapActions({
			appendProductAdded: 'homepage/appendProductAdded',
			funnelDuplicate: 'homepage/funnelDuplicate',
		}),
		getButtonText(platform) {
			if (platform.isLoading) {
				return 'Menambahkan...';
			}
			if (platform.isComingSoon) {
				return 'Segera';
			}
			if (!platform.isEnabled) {
				return 'Segera';
			}
			return 'Pilih';
		},
		getBadgeText(platform) {
			// Hide badge during loading
			if (platform.isLoading) {
				return '';
			}
			// Show badge only if platform is coming soon
			if (platform.isComingSoon) {
				return 'Coming Soon';
			}
			// Don't show badge for other cases
			return '';
		},
		shouldShowButton(platform) {
			// Always show button for Orderonline
			if (platform.key === 'orderonline') {
				return true;
			}
			// For other platforms, only show if enabled
			return platform.isEnabled;
		},
		selectPlatform(platform) {
			if (!platform.isEnabled) {
				return;
			}

			// If Orderonline is selected, add product to OO
			if (platform.key === 'orderonline') {
				this.addProductToOO();
				return;
			}

			this.redirectToMarketplacePage(platform.key);
		},
		addProductToOO() {
			if (!this.productId) {
				return;
			}

			this.isAddingToOrderonline = true;
			this.$emit('loading', true);

			let productId = this.productId;
			if (typeof this.productId === 'object') {
				productId = this.productId.$oid;
			}

			const params = {
				product_id: productId,
				duplicate_funnel: false,
			};

			const callback = (response) => {
				const addedProduct = response.data.added;
				this.appendProductAdded(addedProduct);
				this.funnelDuplicate(addedProduct);

				this.$notify({
					group: 'app',
					title: 'Success add product',
					text: `Berhasil menambah produk ${this.productName}`,
					type: 'success',
				});

				this.$emit('success', response);
				this.handleClose();
			};

			const errorCallback = (e) => {
				let message = getAxiosErrorMessage(e);
				message = e.response.status === 400 && message === 'invalid_connection' ? '<span>Account connection is not valid. <a class="disabled-style-link text-white" href="/settings/connections">Click here to reconnect account.</a></span>' : message;
				this.$notify({
					group: 'app',
					title: 'Failed add product',
					text: message,
					type: 'error',
				});

				this.isAddingToOrderonline = false;
				this.$emit('loading', false);
				this.$emit('error', e);
			};

			orderonlineApi.addDropshipProduct(params, callback, errorCallback).finally(() => {
				this.isAddingToOrderonline = false;
				this.$emit('loading', false);
			});
		},
		handleClose() {
			this.resetState();
			this.$emit('close');
		},
		getProductId() {
			if (!this.productId) {
				return '';
			}
			let productId = this.productId;
			if (typeof this.productId === 'object') {
				productId = this.productId.$oid;
			}
			return productId;
		},
		getProductUserId() {
			if (!this.productUserId) {
				return null;
			}
			let userId = this.productUserId;
			if (typeof this.productUserId === 'object') {
				userId = this.productUserId.$oid;
			}
			return userId;
		},
		resetState() {
			this.isAddingToOrderonline = false;
		},
		redirectToMarketplacePage(channel) {
			if (!this.productSlug || !this.productUserId) {
				this.$notify({
					group: 'app',
					title: 'Error',
					text: 'Produk tidak ditemukan',
					type: 'error',
				});
				return;
			}

			// Close the modal
			this.handleClose();

			// Redirect to the marketplace page in new tab
			const url = this.$router.resolve({
				name: 'add-to-marketplace',
				params: { channel, productSlug: this.productSlug },
			}).href;

			let userId = this.productUserId;
			if (typeof this.productUserId === 'object') {
				userId = this.productUserId.$oid;
			}
			const encodedUserId = btoa(userId);

			window.open(`${url}-${encodedUserId}`, '_blank');
		},
	},
};
</script>

<style scoped>
.platform-selection__subtitle {
	color: #6c757d;
	margin-bottom: 1rem;
}

.platform-selection__grid {
	display: grid;
	grid-template-columns: 1fr;
	grid-gap: 0.75rem;
}

.platform-card {
	display: flex;
	align-items: center;
	padding: 1rem;
	border: 1px solid #e1e4e8;
	border-radius: 12px;
	background-color: #fff;
	cursor: pointer;
	transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.platform-card:focus {
	outline: none;
	box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.platform-card:hover {
	border-color: #0d6efd;
	box-shadow: 0 4px 12px rgba(13, 110, 253, 0.08);
}

.platform-card--disabled {
	cursor: not-allowed;
	opacity: 0.6;
}

.platform-card--disabled:hover {
	border-color: #e1e4e8;
	box-shadow: none;
}

.platform-card__image {
	width: 56px;
	height: 56px;
	border-radius: 10px;
	background-color: #f8f9fa;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 1rem;
	overflow: hidden;
}

.platform-card__image img {
	width: 100%;
	height: 100%;
	object-fit: contain;
}

.platform-card__placeholder {
	color: #6c757d;
}

.platform-card__content {
	flex: 1;
	text-align: left;
}

.platform-card__title {
	display: flex;
	align-items: center;
	font-weight: 600;
	margin-bottom: 0.25rem;
	gap: 0.5rem;
}


.platform-card__description {
	color: #6c757d;
	margin: 0;
	font-size: 0.875rem;
}

.platform-card__action {
	margin-left: 1rem;
}
</style>
